import React, { useState } from "react";
import { Stack, Tab, Typography } from "@mui/material";
import { TabContext } from "@mui/lab";
import { MarketConnect, MarketSecurityProps, MarketView, RangeStart } from "../../../_app/types";
import PageLayout from "../../../_components/PageLayout";
import Header from "../../../_modules/Header/Header";
import { TabsStyled } from "../../../_components/TabsStyled";
import { TabPanelStyled } from "../../../_components/TabPanelStyled";
import AdronTypography from "../../../_components/AdronTypography";
import Spacer from "../../../_components/Spacer";
import ConnectAccountForm from "./ConnectAccountForm";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { useAppNavigate } from "../../../_hooks/use-app-navigate";
import { useApi } from "../../../_hooks/use-api";
import { showNotification } from "../../../store/slices/notificationSlice";
import { setAccounts, switchAccount } from "../../../store/slices/userSlice";
import { HubError } from "../../../class/Error";
import MyButton from "../../../_components/MyButton";

interface Props {
  market: MarketView;
}

const ConnectAccount = ({ market }: Props) => {
  const { t } = useTranslation("account");

  const [connectType, setConnectType] = useState<string>("api");

  const user = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const api = useApi();
  const [isLoading, setIsLoading] = useState(false);

  const [marketConnect, setMarketConnect] = useState<MarketConnect>({
    name: "",
    market: market.name,
    leverage: 1,
    marketSecurityProps: {},
  });

  const connectAccount = async (marketConnect: MarketConnect) => {
    setIsLoading(true);

    try {
      const response = await api.connectNewAccount(user.profile.profileId, marketConnect);

      dispatch(switchAccount(response));
      dispatch(setAccounts([...user.profile.accounts, response]));

      navigate.bots();
    } catch (error: any) {
      error instanceof HubError
        ? dispatch(
            showNotification({
              message: `${error.message} ${error.detailedMessage}`,
              severity: "error",
            })
          )
        : dispatch(showNotification({ message: error.message, severity: "error" }));
    }
    setIsLoading(false);
  };

  const changeConnectType = (_: React.SyntheticEvent, newValue: RangeStart) =>
    setConnectType(newValue as string);

  const handleMarketSecurityProps = <K extends keyof MarketSecurityProps>(
    key: K,
    value: MarketSecurityProps[K]
  ) => {
    setMarketConnect((prevState) => ({
      ...prevState,
      marketSecurityProps: {
        ...prevState.marketSecurityProps,
        [key]: value,
      },
    }));
  };

  const handleAccountName = (value: string) =>
    setMarketConnect((prevState) => ({ ...prevState, name: value }));

  const handleLeverage = (value: number) =>
    setMarketConnect((prevState) => ({ ...prevState, leverage: value }));

  return (
    <PageLayout
      contentSx={{ padding: "20px 8px 0" }}
      header={<Header pageTitle={t("addAccount")} needBack />}
      bottomButton={
        <MyButton
          isLoading={isLoading}
          onClick={() => connectAccount(marketConnect)}
          variant="primary"
          withPaper
        >
          {t("connectAccount")}
        </MyButton>
      }
    >
      <TabContext value={connectType}>
        <TabsStyled style={{ borderRadius: 12, width: "100%" }} onChange={changeConnectType}>
          <Tab
            style={{ maxWidth: "100%" }}
            label={t("fastConnect")}
            disabled
            value={"fastConnect"}
            className="AdronSecondaryTabs"
          />
          <Tab
            style={{ maxWidth: "100%" }}
            label={t("apiKeys")}
            value={"api"}
            className="AdronSecondaryTabs"
          />
        </TabsStyled>

        <TabPanelStyled noPadding={true} value="fastConnect" />
        <TabPanelStyled noPadding={true} value="api">
          <Stack direction={"column"} spacing={2}>
            <Typography sx={{ paddingBottom: 1 }} variant="h2" color="textSecondary">
              {t("connectKeys")}
            </Typography>
            <Stack direction={"column"} spacing={1} sx={{ paddingBottom: "8px" }}>
              <AdronTypography value={`1. ${t("logIn")}`} />
              <AdronTypography value={`2. ${t("pasteData")}`} />
            </Stack>
            <ConnectAccountForm
              marketConnect={marketConnect}
              market={market.name}
              onChangeAccountName={handleAccountName}
              onChangeLeverage={handleLeverage}
              onChangeMarketSecurityProps={handleMarketSecurityProps}
            />
          </Stack>
        </TabPanelStyled>
      </TabContext>
      <Spacer height="100px" />
    </PageLayout>
  );
};

export default ConnectAccount;
